// frontend/src/components/ai/AIChatInterface.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { settingsApi } from '../../api/settings';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FaTrash, FaTimes, FaPaperPlane, FaPaperclip, FaSpinner, FaRobot, FaImage, FaExternalLinkAlt, FaWindowMinimize, FaWindowMaximize } from 'react-icons/fa';
import { compressImage } from '../../utils/imageCompression';
import uploadService from '../../services/upload';

// Reliable fallback image if the product image fails
const FALLBACK_IMAGE = "https://placehold.co/300x200?text=No+Image+Available";

// Detects whether a URL is an image (handles Firebase URLs with ?alt=media and standard extensions)
const isImageUrl = (url) => {
    if (!url) return false;
    if (url.includes('firebasestorage.googleapis.com')) return true;
    if (url.includes('storage.googleapis.com')) return true;
    if (url.match(/\.(jpeg|jpg|gif|png|webp|svg)(\?.*)?$/i)) return true;
    return false;
};

function AIChatInterface({
    messages,
    onSendMessage,
    isSending,
    error,
    setChatError,
    onClose,
    onClearChat,
    position,
    setPosition,
    visionEnabled = true
}) {
  const { isAuthenticated } = useAuth();
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);
  const chatRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [compressionQuality, setCompressionQuality] = useState(0.5);

  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const isDraggingRef = useRef(isDragging);
  // Tells a click on the minimized circle apart from a drag of it, so
  // dragging the bubble does not also reopen the chat.
  const didDragRef = useRef(false);
  useEffect(() => {
      isDraggingRef.current = isDragging;
  }, [isDragging]);

  // --- FETCH COMPRESSION SETTING ---
  useEffect(() => {
      const fetchSettings = async () => {
          try {
              const settings = await settingsApi.getAllSettings();
              const quality = settings.media_config?.chat_compression || 0.5;
              setCompressionQuality(quality);
          } catch (e) {
              console.error("Failed to fetch chat compression settings", e);
          }
      };
      fetchSettings();
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const scrollContainer = messagesContainerRef.current;
    if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }, [messages, messages.length, isSending]);

  useEffect(() => {
    const chatElement = chatRef.current;
    if (!chatElement) return;

    const calculateAndSetPosition = () => {
        const bounds = chatElement.getBoundingClientRect();
        const elementWidth = bounds.width > 0 ? bounds.width : (window.innerWidth < 768 ? window.innerWidth * 0.9 : 358);
        const elementHeight = bounds.height > 0 ? bounds.height : Math.min(620, window.innerHeight - 80);

        const isMobile = window.innerWidth < 768;
        const initialX = isMobile
            ? (window.innerWidth - elementWidth) / 2
            : window.innerWidth - elementWidth - 16;
        const initialY = window.innerHeight - elementHeight - 16;
        const NAVBAR_HEIGHT = 70; // keep chat below the top navbar
        const finalY = Math.max(NAVBAR_HEIGHT, initialY);
        const finalX = Math.max(16, initialX);

        setPosition({ x: finalX, y: finalY });
    };

    const timeoutId = setTimeout(calculateAndSetPosition, 50);
    const handleResize = () => {
        if (!isDraggingRef.current) {
            calculateAndSetPosition();
        }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [setPosition]);

  useEffect(() => {
      const animFrameRef = { current: null };

      const handleMouseMove = (e) => {
          if (!isDragging) return;
          e.preventDefault();

          const clientX = e.clientX;
          const clientY = e.clientY;
          didDragRef.current = true;

          if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
          animFrameRef.current = requestAnimationFrame(() => {
              const newX = clientX - dragOffset.current.x;
              const newY = clientY - dragOffset.current.y;

              const chatElement = chatRef.current;
              if (!chatElement) return;
              const bounds = chatElement.getBoundingClientRect();
              const boundedX = Math.max(0, Math.min(newX, window.innerWidth - bounds.width));
              const boundedY = Math.max(70, Math.min(newY, window.innerHeight - bounds.height));

              setPosition({ x: boundedX, y: boundedY });
          });
      };

      const handleMouseUp = () => {
          setIsDragging(false);
      };

      if (isDragging) {
          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
      }

      return () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
      };
  }, [isDragging, setPosition]);

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!inputMessage.trim() && !selectedImageFile) || isSending || !isAuthenticated || isUploading) return;

    let imageUrl = null;

    if (selectedImageFile) {
        setIsUploading(true);
        try {
            const response = await uploadService.uploadImage(selectedImageFile, 'ai-uploads');
            imageUrl = response.url;
        } catch (err) {
            setChatError("Failed to upload image.");
            setIsUploading(false);
            return;
        } finally {
            setIsUploading(false);
        }
    }

    onSendMessage(inputMessage.trim(), imageUrl);

    setInputMessage('');
    setSelectedImageFile(null);
    setImagePreviewUrl(null);
  };

  const handleMouseDown = (e) => {
      // When minimized the whole circle IS a button, so the usual
      // "ignore buttons" rule would block dragging entirely.
      if (!isMinimized &&
          (e.target.closest('button') || e.target.closest('input') || e.target.closest('a'))) return;
      didDragRef.current = false;
      setIsDragging(true);
      const bounds = chatRef.current.getBoundingClientRect();
      dragOffset.current = {
          x: e.clientX - bounds.left,
          y: e.clientY - bounds.y,
      };
      e.preventDefault();
  };

  const handleTouchStart = (e) => {
      if (!isMinimized &&
          (e.target.closest('button') || e.target.closest('input') || e.target.closest('a'))) return;
      didDragRef.current = false;
      setIsDragging(true);
      const touch = e.touches[0];
      const bounds = chatRef.current.getBoundingClientRect();
      dragOffset.current = {
          x: touch.clientX - bounds.left,
          y: touch.clientY - bounds.top,
      };
  };

  const touchAnimRef = useRef(null);

  const handleTouchMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const touch = e.touches[0];
      const clientX = touch.clientX;
      const clientY = touch.clientY;

      if (touchAnimRef.current) cancelAnimationFrame(touchAnimRef.current);
      touchAnimRef.current = requestAnimationFrame(() => {
          const newX = clientX - dragOffset.current.x;
          const newY = clientY - dragOffset.current.y;

          const chatElement = chatRef.current;
          if (!chatElement) return;
          const bounds = chatElement.getBoundingClientRect();
          const boundedX = Math.max(0, Math.min(newX, window.innerWidth - bounds.width));
          const boundedY = Math.max(70, Math.min(newY, window.innerHeight - bounds.height));

          setPosition({ x: boundedX, y: boundedY });
      });
  };

  const handleTouchEnd = () => {
      setIsDragging(false);
  };

  const handleFileSelect = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
          const compressed = await compressImage(file, compressionQuality);
          setSelectedImageFile(compressed);
          setImagePreviewUrl(URL.createObjectURL(compressed));
      } catch (err) {
          setChatError("Could not process image file.");
      }
  };

  const handleRemoveImage = () => {
      setSelectedImageFile(null);
      setImagePreviewUrl(null);
      if (fileInputRef.current) {
          fileInputRef.current.value = "";
      }
  };

  // --- ROBUST IMAGE COMPONENT ---
  const ChatImage = ({ src, alt }) => {
    const [imgSrc, setImgSrc] = useState(src);
    const [hasError, setHasError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        setImgSrc(src);
        setHasError(false);
        setRetryCount(0);
    }, [src]);

    const handleError = () => {
        if (hasError) return;

        console.warn(`[ChatImage] Failed to load: ${imgSrc}`);

        // Attempt 1: Try converting Google Storage URL to Firebase Public URL
        if (retryCount === 0 && imgSrc && imgSrc.includes('storage.googleapis.com')) {
            try {
                const urlObj = new URL(imgSrc);
                const pathParts = urlObj.pathname.split('/');
                if (pathParts.length > 2) {
                    const bucket = pathParts[1];
                    const filePath = pathParts.slice(2).join('/');
                    const encodedPath = encodeURIComponent(filePath);
                    const newUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
                    console.log(`[ChatImage] Retrying with converted URL: ${newUrl}`);
                    setImgSrc(newUrl);
                    setRetryCount(1);
                    return;
                }
            } catch (e) {
                console.error("URL conversion failed", e);
            }
        }

        // Attempt 2: Fallback to placeholder
        if (imgSrc !== FALLBACK_IMAGE) {
            console.log("[ChatImage] Switching to fallback image.");
            setImgSrc(FALLBACK_IMAGE);
            setRetryCount(prev => prev + 1);
            return;
        }

        setHasError(true);
    };

    if (!src) return null;

    if (hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-3 border border-red-200 bg-red-50 rounded-lg my-2">
          <FaImage className="text-red-400 text-xl mb-1" />
          <span className="text-xs text-red-600 font-bold">Image unavailable</span>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-blue-600 hover:underline mt-1 flex items-center gap-1"
          >
            Open Direct Link <FaExternalLinkAlt size={8} />
          </a>
        </div>
      );
    }

    return (
      <a href={imgSrc} target="_blank" rel="noopener noreferrer" className="block">
          <img
            src={imgSrc}
            alt={alt || "Chat Image"}
            className="max-w-full h-auto rounded-lg my-2 border border-gray-300 block hover:opacity-95 transition-opacity"
            style={{ maxHeight: '250px' }}
            referrerPolicy="no-referrer"
            onError={handleError}
            loading="lazy"
          />
      </a>
    );
  };

  const getVisibleText = (text) => {
      if (!text) return "";
      let clean = text.replace(/\[IMAGE_CACHE\]:.*?$/s, '').trim();
      clean = clean.replace(/\[ID_CACHE\]:.*?$/s, '').trim();
      return clean;
  };

  return (
    <div
        ref={chatRef}
        className="fixed p-0 flex flex-col z-50 w-[90%] max-w-md md:max-w-[358px]"
        
        style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            height: isMinimized ? '64px' : 'calc(100vh - 80px)',
            maxHeight: isMinimized ? '64px' : '620px',
            width: isMinimized ? '64px' : undefined,
            maxWidth: isMinimized ? '64px' : undefined,
            touchAction: 'none',
            willChange: isDragging ? 'transform' : 'auto',
            cursor: isDragging ? 'grabbing' : 'auto',
            userSelect: 'none',
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
            border: '2px solid #1e40af',
            boxShadow: '0 8px 32px rgba(30, 58, 95, 0.35), 0 2px 8px rgba(0,0,0,0.2)',
            borderRadius: isMinimized ? '50%' : '12px',
            overflow: 'hidden',
            transition: 'height 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1), border-radius 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)',
        }}

        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
    >
      {/* HEADER — two completely different layouts for expanded vs minimized */}

      {/* EXPANDED HEADER */}
      {!isMinimized && (
        <div
            className="flex items-center justify-between flex-shrink-0 select-none"
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              background: 'transparent',
              borderBottom: '2px solid #1e40af',
              padding: '10px 12px',
            }}
            onMouseDown={handleMouseDown}
        >
            <h2 className="text-base font-semibold text-white flex items-center gap-2 flex-1 whitespace-nowrap">
                <FaRobot className="text-blue-200 flex-shrink-0" /> AI Mechanic
            </h2>
            <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={(e) => { e.stopPropagation(); setIsMinimized(true); }}
                  className="flex items-center justify-center text-blue-200 hover:text-white transition-colors rounded-full"
                  style={{ width: 32, height: 32, minWidth: 32, minHeight: 32, touchAction: 'manipulation' }}
                  title="Minimise"
                >
                  <FaWindowMinimize size={14} />
                </button>
                <button
                  onClick={onClearChat}
                  className="flex items-center justify-center text-blue-200 hover:text-red-300 transition-colors rounded-full"
                  style={{ width: 32, height: 32, minWidth: 32, minHeight: 32, touchAction: 'manipulation' }}
                  title="Clear Chat History"
                >
                  <FaTrash size={14} />
                </button>
                <button
                  onClick={onClose}
                  className="flex items-center justify-center text-blue-200 hover:text-white transition-colors rounded-full"
                  style={{ width: 32, height: 32, minWidth: 32, minHeight: 32, touchAction: 'manipulation' }}
                  title="Close"
                >
                  <FaTimes size={18} />
                </button>
            </div>
        </div>
      )}

      {/* MINIMIZED — full circle is one big button, drag still works via outer div */}
      {isMinimized && (
        <button
          onClick={(e) => {
              e.stopPropagation();
              // Ignore the click that ends a drag.
              if (didDragRef.current) { didDragRef.current = false; return; }
              setIsMinimized(false);
          }}
          onMouseDown={handleMouseDown}
          className="flex items-center justify-center text-white select-none"
          style={{
            width: '64px',
            height: '64px',
            minWidth: '64px',
            minHeight: '64px',
            touchAction: 'none',
            cursor: isDragging ? 'grabbing' : 'grab',
            background: 'none',
            border: 'none',
            padding: 0,
          }}
          title="Restore AI Mechanic"
        >
          <FaRobot size={26} />
        </button>
      )}

      {/* MESSAGES AREA */}
      {!isMinimized && <div
        ref={messagesContainerRef}
        className="flex-grow overflow-y-auto p-4 space-y-3"
        style={{ background: "#f0f4f8" }}
      >
        {messages.length === 0 && !isSending ? (
          <div className="text-center text-gray-500 italic p-4 flex flex-col items-center justify-center h-full">
            <FaRobot className="text-4xl text-gray-300 mb-2" />
            <p>Hello! I'm your AI Mechanic.</p>
            <p className="text-xs mt-1">Ask me about car problems, parts, or booking a service.</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            const visibleText = getVisibleText(msg.text);
            return (
                <div key={index} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`rounded-lg p-3 mx-1 break-words max-w-[90%] shadow-sm ${msg.sender === 'user' ? 'text-white' : 'text-gray-800'}`}
                    style={msg.sender === 'user' ? { background: 'linear-gradient(135deg, #2563eb, #1e40af)' } : { background: '#ffffff', borderLeft: '3px solid #2563eb' }}>
                    <div className="ai-message-content text-sm">
                        <ReactMarkdown
                           remarkPlugins={[remarkGfm]}
                           components={{
                               a: ({node, ...props}) => {
                                   const href = props.href || '';
                                   if (isImageUrl(href)) {
                                       return <ChatImage src={href} alt={String(props.children) || "Product Image"} />;
                                   }
                                   return (
                                     <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-bold">
                                       {props.children}
                                     </a>
                                   );
                               },
                               p: ({node, ...props}) => <p {...props} className="mb-1 last:mb-0"/>,
                               ul: ({node, ...props}) => <ul {...props} className="list-disc list-inside ml-4 mb-1"/>,
                               ol: ({node, ...props}) => <ol {...props} className="list-decimal list-inside ml-4 mb-1"/>,
                               li: ({node, ...props}) => <li {...props} className="mb-0.5"/>,
                               img: ({node, ...props}) => <ChatImage src={props.src} alt={props.alt} />,
                           }}
                        >
                            {visibleText}
                        </ReactMarkdown>
                    </div>
                  </div>
                </div>
            );
          })
        )}

        {isSending && (
            <div className="flex justify-start animate-pulse">
                <div className="rounded-lg p-3 mx-1 shadow-sm flex items-center gap-2" style={{ background: "#ffffff", borderLeft: "3px solid #2563eb" }}>
                    <FaSpinner className="animate-spin text-primary" />
                    <span className="text-sm font-bold italic">AI is analyzing...</span>
                </div>
            </div>
        )}
        <div ref={messagesEndRef} style={{ height: '1px' }} />
      </div>}

      {/* ERROR DISPLAY */}
      {error && !isMinimized && (
        <div className="px-4 py-2 bg-red-50 text-red-700 text-xs flex-shrink-0" style={{ borderTop: "1px solid #fca5a5" }}>
          {error}
        </div>
      )}

      {/* INPUT AREA */}
      {!isMinimized && <div className="p-3 flex-shrink-0" style={{ background: "#ffffff", borderTop: "2px solid #1e3a5f" }}>
          {imagePreviewUrl && (
              <div className="mb-2 p-2 border rounded-lg relative bg-white inline-block">
                  <img src={imagePreviewUrl} alt="Preview" className="h-12 w-12 object-cover rounded" />
                  <button
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs shadow-sm"
                  >
                      <FaTimes />
                  </button>
              </div>
          )}

          <form onSubmit={handleSend} className="flex space-x-2 items-center">
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/jpeg, image/png, image/webp"
                className="hidden"
            />

            <button
                type="button"
                onClick={() => visionEnabled && fileInputRef.current.click()}
                className={`p-2 rounded-full transition-colors flex-shrink-0
                    ${visionEnabled
                        ? 'text-gray-500 hover:text-primary hover:bg-gray-200'
                        : 'text-gray-300 cursor-not-allowed'
                    }
                `}
                disabled={!visionEnabled || isSending || !isAuthenticated || isUploading}
            >
                {isUploading ? <FaSpinner className="animate-spin" /> : <FaPaperclip size={18} />}
            </button>

            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              className="flex-grow px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              placeholder={isSending ? "Please wait..." : (!isAuthenticated ? "Log in to chat..." : "Type message...")}
              disabled={isSending || !isAuthenticated || isUploading}
            />

            <button
              type="submit"
              className={`bg-primary hover:bg-primary-dark text-white p-2 rounded-full transition-colors flex-shrink-0 shadow-sm ${isSending || !isAuthenticated || isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isSending || !isAuthenticated || isUploading}
            >
              <FaPaperPlane size={16} />
            </button>
          </form>
            {!isAuthenticated && (
                <p className="text-center text-[10px] text-gray-500 mt-1">
                    Login required to chat.
                </p>
            )}
      </div>}
    </div>
  );
}

export default AIChatInterface;